import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  TrendingUp as WinRateIcon,
  Star as RatingIcon,
  ThumbUpAlt as ApprovalIcon,
  Cancel as LossIcon
} from '@mui/icons-material';
import OptimizedImage from './OptimizedImage';
import magicCardIcon from '../assets/magic_card.png';

const StatCard = ({ icon: Icon, title, value, color, customIcon, subtitle }) => (
  <Card sx={{ 
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'background.paper',
    boxShadow: 1,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 2
    }
  }}>
    <CardContent sx={{ 
      display: 'flex', 
      alignItems: 'center',
      gap: 1.5,
      py: 1.5,
      px: 2,
      '&:last-child': { pb: 1.5 }
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        color: color
      }}>
        {customIcon || <Icon sx={{ fontSize: 24 }} />}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const StatsCards = ({ matches, user }) => {
  // Calcular estadísticas solo para partidas aprobadas
  const approvedMatches = matches.filter(match => {
    // Verificar que todos los jugadores hayan aprobado
    const team1Players = match.team1.players;
    const team2Players = match.team2.players;
    
    const allTeam1Approved = team1Players.every(p => p.approval_status === 'approved');
    const allTeam2Approved = team2Players.every(p => p.approval_status === 'approved');
    
    return allTeam1Approved && allTeam2Approved;
  });

  // Calcular estadísticas de aprobación/rechazo
  const userInvolvedMatches = matches.filter(match => {
    // Exclude cancelled matches from this calculation
    if (match.approval_status === 'cancelled') {
      return false;
    }
    const team1Players = match.team1?.players || [];
    const team2Players = match.team2?.players || [];
    return team1Players.some(p => p.user_id === user.id) || 
           team2Players.some(p => p.user_id === user.id);
  });

  const userRejectedMatches = userInvolvedMatches.filter(match => {
    const team1Player = match.team1?.players?.find(p => p.user_id === user.id);
    const team2Player = match.team2?.players?.find(p => p.user_id === user.id);
    return (team1Player?.approval_status === 'rejected' || team2Player?.approval_status === 'rejected');
  });

  const totalInvolved = userInvolvedMatches.length;
  const totalRejected = userRejectedMatches.length;
  const approvalRate = totalInvolved > 0 ? ((totalInvolved - totalRejected) / totalInvolved) * 100 : 100;

  const getApprovalColor = (rate) => {
    if (rate >= 90) return '#4CAF50';
    if (rate >= 70) return '#FFC107';
    return '#F44336';
  };

  const getApprovalText = (rate) => {
    if (rate >= 90) return 'Excelente';
    if (rate >= 70) return 'Regular';
    return 'Bajo';
  };

  const totalMatches = approvedMatches.length;
  const wonMatches = approvedMatches.filter(match => {
    const userTeam = match.team1.players.find(p => p.user_id === user.id) 
      ? match.team1 
      : match.team2.players.find(p => p.user_id === user.id) 
        ? match.team2 
        : null;

    if (!userTeam) return false;
    const otherTeam = userTeam.team_number === 1 ? match.team2 : match.team1;
    return userTeam.score > otherTeam.score;
  }).length;

  const lostMatches = totalMatches - wonMatches;
  const winrate = totalMatches > 0 ? Math.round((wonMatches / totalMatches) * 100) : 0;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Grid container spacing={1.5} sx={{ mt: 1 }}>
        {/* Partidas Totales */}
        <Grid item xs={6} sm={4}>
          <StatCard
            icon={GameIcon}
            title="Partidas"
            value={totalMatches}
            color="#2196F3"
          />
        </Grid>

        {/* Win Rate */}
        <Grid item xs={6} sm={4}>
          <StatCard
            icon={WinRateIcon}
            title="Win Rate"
            value={`${winrate}%`}
            color="#FF9800"
          />
        </Grid>

        {/* Victorias */}
        <Grid item xs={6} sm={4}>
          <StatCard
            icon={TrophyIcon}
            title="Victorias"
            value={wonMatches}
            color="#4CAF50"
          />
        </Grid>

        {/* Derrotas */}
        <Grid item xs={6} sm={4}>
          <StatCard
            icon={LossIcon}
            title="Derrotas"
            value={lostMatches}
            color="#F44336"
          />
        </Grid>

        {/* Mitos Rating */}
        <Grid item xs={6} sm={4}>
          <StatCard
            icon={RatingIcon}
            title="Mitos Rating"
            value={<Typography component="span" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>Coming Soon</Typography>}
            color="#9C27B0"
            customIcon={
              <Typography 
                sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: '#9C27B0',
                  letterSpacing: '0.5px'
                }}
              >
                MR
              </Typography>
            }
          />
        </Grid>

        {/* Tasa de Aprobación */}
        <Grid item xs={6} sm={4}>
          <Tooltip title={`Tasa de Aprobación: ${Math.round(approvalRate)}%`}>
            <Card sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'background.paper',
              boxShadow: 1,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}>
              <CardContent sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: 2,
                width: '100%',
                '&:last-child': { pb: 1.5 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: getApprovalColor(approvalRate)
                }}>
                  <ApprovalIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    Aprobación
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={approvalRate}
                      sx={{
                        width: '100%',
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getApprovalColor(approvalRate),
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: getApprovalColor(approvalRate), mt: 0.5, display: 'block' }}>
                    {getApprovalText(approvalRate)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatsCards; 